import { hasOwnProperty } from "@glideapps/ts-necessities";
import { getCloudRegion } from "./function-region";

import { logError } from "./debug-print";
import { exceptionToString, getCurrentTimestampInMilliseconds } from "./Support";

export type FrontendEventFields = Record<string, string | boolean | number | undefined>;

export type FrontendEventSender = (fields: FrontendEventFields) => void;

let sendEvent: FrontendEventSender | undefined;
let inBuilder: boolean = false;

const appIDRegexp = /.+\/(app|play)\/([^/]+)(\/|$)/;

function getAppID(): string | undefined {
    if (hasOwnProperty(window, "appID")) {
        const { appID } = window;
        if (typeof appID === "string") {
            return appID;
        }
    }

    const match = window.location.href.match(appIDRegexp);
    if (match !== null) {
        return match[2];
    }

    return undefined;
}

function getStandardFields(name: string, appID: string | undefined, duration: number): FrontendEventFields {
    const fields: FrontendEventFields = {
        source: inBuilder ? "builder" : "player",
        name,
        duration_ms: duration,
        browser_location: window.location.href,
        region: getCloudRegion(),
        deployment_version: (window as any).glideDeploymentVersion,
    };
    if (appID !== undefined) {
        fields.app_id = appID;
    }
    return fields;
}

export function frontendSendEvent(name: string, duration: number, fields: FrontendEventFields): void {
    if (sendEvent === undefined) return;

    const appID = getAppID();
    if (appID === undefined) return;

    sendEvent({
        ...getStandardFields(name, appID, duration),
        ...fields,
    });
}

export function frontendTraceUntilEventLoop(name: string, fields: FrontendEventFields): void {
    if (sendEvent === undefined) return;

    const now = Date.now();
    setTimeout(() => {
        const duration = Date.now() - now;
        // We don't want to be overwhelmed by events, so we only trace if it takes
        // at least one second.
        if (duration < 1000) return;
        frontendSendEvent(name, duration, fields);
    });
}

export async function frontendTrace<T>(
    name: string,
    fields: FrontendEventFields | undefined,
    f: (extraFields: FrontendEventFields) => Promise<T>,
    fromBuilder: boolean | "always" = false
): Promise<T> {
    // We always trace `function`, whether we're in the player or in the
    // builder.
    if (sendEvent === undefined || (fromBuilder !== "always" && inBuilder !== fromBuilder && name !== "function")) {
        return await f({});
    }

    const extraFields: FrontendEventFields = {};

    const appID = getAppID();
    if (appID === undefined && fromBuilder === false) {
        return await f(extraFields);
    }

    const start = getCurrentTimestampInMilliseconds();
    let exception: string | undefined;
    try {
        return await f(extraFields);
    } catch (e: unknown) {
        exception = exceptionToString(e);
        throw e;
    } finally {
        try {
            const duration = getCurrentTimestampInMilliseconds() - start;
            sendEvent({
                ...getStandardFields(name, appID, duration),
                exception,
                ...fields,
                ...extraFields,
            });
        } catch (e: unknown) {
            logError("Exception thrown when sending trace", e);
        }
    }
}

export async function maybeFrontendTrace<T>(
    name: string,
    fields: FrontendEventFields | undefined,
    p: number,
    f: (extraFields: FrontendEventFields) => Promise<T>
): Promise<T> {
    if (Math.random() < p) {
        return await frontendTrace(name, fields, f);
    } else {
        return await f({});
    }
}

export function registerFrontendEventSender(s: FrontendEventSender, areWeInBuilder: boolean): void {
    sendEvent = s;
    inBuilder = areWeInBuilder;
}
